import { NAV_GROUPS, type NavGroup, type NavItem } from '@/components/layout/nav-config';

export type NavKind = 'list' | 'create' | 'detail';

export interface NavCatalogEntry {
  /** Stable id derived from path (e.g. "sales.deliveries"). Survives label
   *  renames and is what gets persisted. */
  id: string;
  labelKey: string;
  sectionLabelKey: string;
  /** Base path the user navigates to (e.g. /sales/deliveries). */
  path: string;
  /** Path of the dedicated create route (e.g. /sales/deliveries/new), if any. */
  createPath?: string;
  /** True when there is a `:id` detail route for this entry. */
  hasDetail: boolean;
  /** Toggles to render in the settings page for this entry. */
  kinds: NavKind[];
}

/** Hand-curated set of entry ids that have a `/:id` detail route in App.tsx.
 *  Keeping this here rather than parsing App.tsx is cheap and explicit; if a
 *  new detail route gets added the entry simply won't show a "Detail" toggle
 *  until this list is updated (degradation is graceful — no crash). */
const DETAIL_PATH_IDS = new Set<string>([
  'sales.quotes', 'sales.orders', 'sales.deliveries', 'sales.invoices', 'sales.creditnotes',
  'purchases.orders', 'purchases.receptions', 'purchases.invoices', 'purchases.creditnotes',
  'clients', 'suppliers', 'representatives', 'articles',
  'treasury', 'caisse.sessions',
  'storefront.orders', 'storefront.customers', 'storefront.products', 'storefront.pages',
  'pro.customers',
  'withholding.sales', 'withholding.purchases',
]);

function pathToId(path: string): string {
  if (path === '/') return 'dashboard';
  return path.replace(/^\//, '').replace(/-/g, '').replace(/\//g, '.');
}

function build(): NavCatalogEntry[] {
  const entries: NavCatalogEntry[] = [];
  // Dedupe by id — some parents (e.g. /articles, /stock) reuse the same path
  // as a child, which would otherwise produce duplicate toggles.
  const seen = new Set<string>();

  const walk = (items: NavItem[], section: NavGroup) => {
    for (const it of items) {
      const id = pathToId(it.path);
      if (!seen.has(id)) {
        seen.add(id);
        const hasDetail = DETAIL_PATH_IDS.has(id);
        const kinds: NavKind[] = ['list'];
        if (it.addPath) kinds.push('create');
        if (hasDetail) kinds.push('detail');

        entries.push({
          id,
          labelKey: it.labelKey,
          sectionLabelKey: section.labelKey ?? 'nav.groups.main',
          path: it.path,
          createPath: it.addPath,
          hasDetail,
          kinds,
        });
      }

      if (it.children) walk(it.children, section);
    }
  };

  for (const group of NAV_GROUPS) walk(group.items, group);
  return entries;
}

export const NAV_CATALOG: NavCatalogEntry[] = build();

export function findEntryById(id: string): NavCatalogEntry | undefined {
  return NAV_CATALOG.find((e) => e.id === id);
}

/** Group catalog entries by section, preserving the original order. */
export function groupCatalogBySection(): Array<{ sectionLabelKey: string; items: NavCatalogEntry[] }> {
  const order: string[] = [];
  const map = new Map<string, NavCatalogEntry[]>();
  for (const entry of NAV_CATALOG) {
    if (!map.has(entry.sectionLabelKey)) {
      order.push(entry.sectionLabelKey);
      map.set(entry.sectionLabelKey, []);
    }
    map.get(entry.sectionLabelKey)!.push(entry);
  }
  return order.map((k) => ({ sectionLabelKey: k, items: map.get(k)! }));
}
